import { styled } from '@linaria/react'
import React, { type FC, useState, type ReactNode, useEffect } from 'react'
import { colors } from '../styles/colors'
import { useTelegram } from '../hooks/useTelegram'

export const Slider: FC<{
  slides: ReactNode[]
}> = ({ slides }) => {
  const { tg } = useTelegram()
  const [currentSlide, setCurrentSlide] = useState(0)
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [touchEnd, setTouchEnd] = useState<number | null>(null)

  // Минимальное расстояние свайпа для переключения слайда
  const minSwipeDistance = 50

  // Отключаем вертикальные свайпы в Telegram Mini App
  useEffect(() => {
    tg.disableVerticalSwipes()
    // Включаем обратно при размонтировании компонента (опционально)
    return () => {
      tg.enableVerticalSwipes()
    }
  }, [tg])

  const handleDotClick = (index: number) => {
    setCurrentSlide(index)
  }

  const onTouchStart = (e: React.TouchEvent) => {
    e.preventDefault()
    setTouchEnd(null)
    const firstTouch = e.targetTouches[0]
    if (firstTouch) {
      setTouchStart(firstTouch.clientX)
    }
  }

  const onTouchMove = (e: React.TouchEvent) => {
    e.preventDefault()
    const firstTouch = e.targetTouches[0]
    if (firstTouch) {
      setTouchEnd(firstTouch.clientX)
    }
  }

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) {
      return
    }

    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance

    if (isLeftSwipe && currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1)
    }

    if (isRightSwipe && currentSlide > 0) {
      setCurrentSlide(currentSlide - 1)
    }
  }

  return (
    <SliderContainer>
      <SliderViewport>
        <SlidesWrapper
          currentSlide={currentSlide}
          slidesCount={slides.length}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {slides.map((slide, index) => (
            <Slide key={index} isActive={index === currentSlide}>
              {slide}
            </Slide>
          ))}
        </SlidesWrapper>
      </SliderViewport>

      {slides.length > 1 && (
        <DotsContainer>
          {slides.map((_, index) => (
            <Dot
              key={index}
              isActive={index === currentSlide}
              onClick={() => handleDotClick(index)}
            />
          ))}
        </DotsContainer>
      )}
    </SliderContainer>
  )
}

const SliderContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
`

const SliderViewport = styled.div`
  overflow: hidden;
  width: 100%;
`

const SlidesWrapper = styled.div<{ currentSlide: number; slidesCount: number }>`
  display: flex;
  transform: ${props => `translateX(-${props.currentSlide * 100}%)`};
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  touch-action: pan-y;
`

const Slide = styled.div<{ isActive: boolean }>`
  min-width: 100%;
  width: 100%;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: ${props => (props.isActive ? 1 : 0)};
  transition: opacity 0.5s ease;
`

const DotsContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 48px;
  justify-content: center;
`

const Dot = styled.button<{ isActive: boolean }>`
  width: ${props => (props.isActive ? '24px' : '8px')};
  height: 8px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  background: ${colors.secondary};
  opacity: ${props => (props.isActive ? 1 : 0.2)};

  &:hover {
    opacity: 0.6;
  }
`
